import { useEffect, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Badge, Button, Card, CardContent, Textarea } from '@mlm/ui';
import {
  getQuestionThread,
  hideQuestion,
  listInbox,
  replyToQuestion,
  type InboxQuestion,
} from '../lib/api';
import { QueryError } from '../components/query-error';

/** Q&A inbox — every learner question across the creator's courses, newest first. */
export function QaPage() {
  const qc = useQueryClient();
  // `?question=` arrives from a `creator_question` bell. Driven by an effect,
  // not a useState initialiser: the bell lives in the persistent shell, so a
  // second click while already on this page only changes the search string.
  const [params] = useSearchParams();
  const wanted = params.get('question');
  const [openId, setOpenId] = useState<string | null>(wanted);
  const [filter, setFilter] = useState<'unanswered' | 'all'>(wanted ? 'all' : 'unanswered');
  useEffect(() => {
    if (!wanted) return;
    setOpenId(wanted);
    setFilter('all');
  }, [wanted]);

  // The server pages and filters, so "Unanswered" covers every question and
  // not just the ones already loaded.
  const inbox = useInfiniteQuery({
    queryKey: ['creator-inbox', filter],
    queryFn: ({ pageParam }) =>
      listInbox({ unanswered: filter === 'unanswered', offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last) => last.nextOffset ?? undefined,
  });
  const rows = inbox.data?.pages.flatMap((p) => p.items) ?? [];

  const hide = useMutation({
    mutationFn: ({ id, hidden }: { id: string; hidden: boolean }) => hideQuestion(id, hidden),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['creator-inbox'] }),
  });

  return (
    <div className="max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Questions</h1>
        <div className="flex gap-1 rounded border p-0.5 text-xs">
          <button
            className={`rounded px-2 py-1 ${filter === 'unanswered' ? 'bg-accent font-medium' : ''}`}
            onClick={() => setFilter('unanswered')}
          >
            Unanswered
          </button>
          <button
            className={`rounded px-2 py-1 ${filter === 'all' ? 'bg-accent font-medium' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
        </div>
      </div>
      <Card>
        <CardContent className="p-0">
          {inbox.isError ? (
            <div className="p-4">
              <QueryError
                what="your questions"
                error={inbox.error}
                onRetry={() => void inbox.refetch()}
              />
            </div>
          ) : inbox.isLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {filter === 'unanswered'
                ? 'All caught up — no unanswered questions.'
                : 'No questions yet.'}
            </p>
          ) : (
            <ul className="divide-y">
              {rows.map((r) => (
                <QuestionRow
                  key={r.id}
                  row={r}
                  open={openId === r.id}
                  onOpen={() => setOpenId(r.id)}
                  onClose={() => setOpenId(null)}
                  onHide={() => hide.mutate({ id: r.id, hidden: !r.hidden })}
                />
              ))}
            </ul>
          )}
          {inbox.hasNextPage && (
            <div className="border-t p-3 text-center">
              <Button
                size="sm"
                variant="outline"
                disabled={inbox.isFetchingNextPage}
                onClick={() => void inbox.fetchNextPage()}
              >
                {inbox.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function QuestionRow({
  row: r,
  open,
  onOpen,
  onClose,
  onHide,
}: {
  row: InboxQuestion;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onHide: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  // The thread loads when the question is opened: the creator answers with the
  // learners' follow-ups in view, not blind.
  const thread = useQuery({
    queryKey: ['creator-thread', r.id],
    queryFn: () => getQuestionThread(r.id),
    enabled: open && r.replyCount > 0,
  });
  const reply = useMutation({
    meta: { successMessage: 'Reply posted.' },
    mutationFn: () => replyToQuestion(r.id, draft.trim()),
    onSuccess: () => {
      setDraft('');
      onClose();
      qc.invalidateQueries({ queryKey: ['creator-inbox'] });
      qc.invalidateQueries({ queryKey: ['creator-thread', r.id] });
    },
  });

  return (
    <li className={`p-4 ${r.hidden ? 'opacity-60' : ''}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{r.authorName ?? 'Learner'}</span>
        <span>· {new Date(r.createdAt).toLocaleString()}</span>
        <span>·</span>
        <Link to={`/courses/${r.courseId}`} className="hover:underline">
          {r.lessonTitle}
        </Link>
        {r.creatorReplied && <Badge variant="outline">Answered</Badge>}
        {r.hidden && <Badge variant="secondary">Hidden</Badge>}
        {r.replyCount > 0 && (
          <span>
            · {r.replyCount} repl{r.replyCount === 1 ? 'y' : 'ies'}
          </span>
        )}
      </div>
      <p className="mt-1 whitespace-pre-line text-sm">{r.body}</p>
      {open ? (
        <div className="mt-3 space-y-2">
          {r.replyCount > 0 && (
            <div className="space-y-2 border-l-2 pl-3">
              {thread.isError ? (
                <QueryError
                  what="the replies"
                  error={thread.error}
                  onRetry={() => void thread.refetch()}
                />
              ) : thread.isLoading ? (
                <p className="text-xs text-muted-foreground">Loading replies…</p>
              ) : (
                (thread.data ?? []).map((t) => (
                  <div key={t.id} className={t.hidden ? 'opacity-60' : ''}>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">
                        {t.isCreatorReply ? 'You' : (t.authorName ?? 'Learner')}
                      </span>
                      <span>· {new Date(t.createdAt).toLocaleString()}</span>
                      {t.hidden && <Badge variant="secondary">Hidden</Badge>}
                    </div>
                    <p className="whitespace-pre-line text-sm">{t.body}</p>
                  </div>
                ))
              )}
            </div>
          )}
          {r.hidden ? (
            <p className="text-xs text-muted-foreground">
              This question is hidden, so it takes no new replies. Unhide it to answer.
            </p>
          ) : (
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Write your answer…"
            />
          )}
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={onClose}>
              Close
            </Button>
            {!r.hidden && (
              <Button
                size="sm"
                onClick={() => reply.mutate()}
                disabled={!draft.trim() || reply.isPending}
              >
                Post answer
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-2 flex gap-3 text-xs">
          <button className="font-medium text-primary hover:underline" onClick={onOpen}>
            {r.replyCount > 0 ? 'Open thread' : 'Answer'}
          </button>
          <button className="text-muted-foreground hover:underline" onClick={onHide}>
            {r.hidden ? 'Unhide' : 'Hide'}
          </button>
        </div>
      )}
    </li>
  );
}
